import { DefaultPriorityGridRequest, DefaultPriorityGridSlot, DefaultPriorityGridWarehouse, DefaultPriorityType } from '@/console/api/types'

/**
 * LOGISPRD-15812 — 디폴트 센터 우선순위 일괄 반영의 입력 검증과 미리보기(순수 모듈).
 *
 * 운영 요청(구글시트)을 AI 로 요청 본문 JSON 으로 뽑아 붙여 넣는 사용을 전제로 한다. 그래서 화면이 값을 고쳐 주지 않는다.
 * 본문은 붙여 넣은 그대로 보내고, 서버 계약(LLD-outbound-default-priority-grid 3.1절)에 어긋나는 곳만 짚어 반영을 막는다.
 * 하나라도 어긋나면 서버도 전체를 거절하므로(한 트랜잭션), 미리 막아 왕복을 줄이는 것이 목적이다. 최종 판정은 서버다.
 */

export const MAX_SLOTS = 24

const PRIORITY_TYPES: readonly DefaultPriorityType[] = ['WAREHOUSE_PRIORITY', 'PACKING']

/** 정시만 받는다(분·초 0). 서버가 `SCHEDULE_INVALID_SLOT_GRID` 로 거절하는 조건과 같다. */
const ON_THE_HOUR = /^([01]\d|2[0-3]):00:00$/

export type GridParseResult = { request: DefaultPriorityGridRequest; errors: [] } | { request: null; errors: string[] }

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value)

const isNonBlankString = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0

const validateWarehouses = (path: string, value: unknown, errors: string[]): void => {
  if (value === undefined) return
  if (!Array.isArray(value)) {
    errors.push(`${path}.warehouses 는 배열이어야 해요.`)
    return
  }

  const codes = new Set<string>()
  const priorities = new Set<number>()
  value.forEach((warehouse, index) => {
    const at = `${path}.warehouses[${index}]`
    if (!isRecord(warehouse)) {
      errors.push(`${at} 는 객체여야 해요.`)
      return
    }
    const { warehouseCode, priority } = warehouse
    if (!isNonBlankString(warehouseCode)) {
      errors.push(`${at}.warehouseCode 가 비었어요.`)
    } else if (codes.has(warehouseCode)) {
      errors.push(`${at}.warehouseCode ${warehouseCode} 가 슬롯 안에서 중복돼요.`)
    } else {
      codes.add(warehouseCode)
    }
    if (priority === null || priority === undefined) return
    if (typeof priority !== 'number' || !Number.isInteger(priority) || priority < 1) {
      errors.push(`${at}.priority 는 1 이상 정수 또는 null 이어야 해요.`)
    } else if (priorities.has(priority)) {
      errors.push(`${at}.priority ${priority} 가 슬롯 안에서 중복돼요.`)
    } else {
      priorities.add(priority)
    }
  })
}

const validateSlot = (slot: unknown, index: number, startTimes: Set<string>, errors: string[]): void => {
  const path = `slots[${index}]`
  if (!isRecord(slot)) {
    errors.push(`${path} 는 객체여야 해요.`)
    return
  }

  const { startTime, priorityType, warehousePriorityEnabled, warehouses } = slot
  if (typeof startTime !== 'string' || !ON_THE_HOUR.test(startTime)) {
    errors.push(`${path}.startTime 은 정시 HH:00:00 이어야 해요.`)
  } else if (startTimes.has(startTime)) {
    errors.push(`${path}.startTime ${startTime} 이 중복돼요.`)
  } else {
    startTimes.add(startTime)
  }
  if (!PRIORITY_TYPES.includes(priorityType as DefaultPriorityType)) {
    errors.push(`${path}.priorityType 은 ${PRIORITY_TYPES.join(' 또는 ')} 이어야 해요.`)
  }
  // 서버는 이 값을 생략하면 400 이다. 슬롯 최종 상태로 덮어쓰므로 기본값을 두지 않는다.
  if (typeof warehousePriorityEnabled !== 'boolean') {
    errors.push(`${path}.warehousePriorityEnabled 는 true 또는 false 여야 해요.`)
  }
  validateWarehouses(path, warehouses, errors)
}

export const parseGridPayload = (text: string): GridParseResult => {
  if (text.trim().length === 0) return { request: null, errors: [] }

  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch (error) {
    return { request: null, errors: [`JSON 형식이 아니에요: ${(error as Error).message}`] }
  }

  if (!isRecord(parsed)) return { request: null, errors: ['최상위는 객체여야 해요.'] }

  const errors: string[] = []
  const { shipperCode, cutType, slots } = parsed
  if (!isNonBlankString(shipperCode)) errors.push('shipperCode 가 비었어요.')
  if (!isNonBlankString(cutType)) errors.push('cutType 이 비었어요.')
  if (!Array.isArray(slots) || slots.length === 0 || slots.length > MAX_SLOTS) {
    errors.push(`slots 는 1~${MAX_SLOTS}개 배열이어야 해요.`)
  } else {
    const startTimes = new Set<string>()
    slots.forEach((slot, index) => validateSlot(slot, index, startTimes, errors))
  }

  if (errors.length > 0) return { request: null, errors }
  return { request: parsed as DefaultPriorityGridRequest, errors: [] }
}

export type GridPreviewRow = {
  startTime: string
  priorityType: DefaultPriorityType
  warehousePriorityEnabled: boolean
  /** priority 오름차순 센터 코드. */
  ranked: { warehouseCode: string; priority: number }[]
  /** priority 를 null 로 명시한 센터. 요청에 아예 없는 센터도 서버가 null 로 초기화하지만, 그 목록은 서버만 안다. */
  cleared: string[]
}

const byPriority = (a: { priority: number }, b: { priority: number }): number => a.priority - b.priority

const toPreviewRow = (slot: DefaultPriorityGridSlot): GridPreviewRow => {
  const warehouses: DefaultPriorityGridWarehouse[] = slot.warehouses ?? []
  return {
    startTime: slot.startTime,
    priorityType: slot.priorityType,
    warehousePriorityEnabled: slot.warehousePriorityEnabled,
    ranked: warehouses
      .filter((w): w is { warehouseCode: string; priority: number } => w.priority !== null && w.priority !== undefined)
      .sort(byPriority),
    cleared: warehouses.filter((w) => w.priority === null || w.priority === undefined).map((w) => w.warehouseCode),
  }
}

/** 서버가 슬롯을 시작 시각 순으로 반영하므로 미리보기도 같은 순서로 보여 준다. */
export const toPreviewRows = (request: DefaultPriorityGridRequest): GridPreviewRow[] =>
  [...request.slots].sort((a, b) => a.startTime.localeCompare(b.startTime)).map(toPreviewRow)

export const isSuccessResponse = (response: { meta?: { result?: string } } | null | undefined): boolean => response?.meta?.result === 'SUCCESS'
